import React from "react"

import { useTranslation } from "react-i18next"
import { Handle, Position } from "reactflow"

import { Grid } from "@mui/material"
import { Typography } from "@synapse-analytics/synapse-ui"

import { getComponentTypeFromNode } from "../../../../Predictions/helpers"
import { NODE_TYPES } from "../../../types/constants"
import type { NodeConfig, NodeSimpleChipProps } from "../../../types/interfaces"

import styles from "../Nodes.module.scss"

type NodeType = (typeof NODE_TYPES)[keyof typeof NODE_TYPES]

// --- Get node Configurations ---
const getNodeConfigs = (
  t: (key: string) => string,
): Record<NodeType, (data: NodeSimpleChipProps["data"]) => NodeConfig> => {
  const endResultConfig = (data: NodeSimpleChipProps["data"]): NodeConfig => ({
    nodeTitle: t("nodeSimpleChip.nodeTitles.end"),
    classname: "notSelectableChip",
    textColorClassname: "notSelectableText",
    nodeName: data.return_label?.name,
  })

  return {
    [NODE_TYPES.START]: () => ({
      nodeTitle: t("nodeSimpleChip.nodeTitles.start"),
      classname: "notSelectableChip",
      textColorClassname: "notSelectableText",
    }),
    [NODE_TYPES.RULESET]: (data) => ({
      nodeTitle: t("nodeSimpleChip.nodeTitles.ruleset"),
      classname: "rulesetChip",
      textColorClassname: "rulesetChipText",
      nodeName: data.rulesetName,
    }),
    [NODE_TYPES.DECISION]: (data) => ({
      nodeTitle: t("nodeSimpleChip.nodeTitles.decision"),
      classname: "decisionChip",
      textColorClassname: "decisionChipText",
      nodeName: data.rulesetName,
    }),
    [NODE_TYPES.SCORECARD_SET]: (data) => ({
      nodeTitle: t("nodeSimpleChip.nodeTitles.scorecardset"),
      classname: "scorecardsetChip",
      textColorClassname: "scorecardsetChipText",
      nodeName: data.scorecard_set?.name,
    }),
    [NODE_TYPES.PROGRAM]: (data) => ({
      nodeTitle: t("nodeSimpleChip.nodeTitles.program"),
      classname: "programsChip",
      textColorClassname: "programChipText",
      nodeName: data.program?.name,
    }),
    [NODE_TYPES.TAGLIST]: (data) => ({
      nodeTitle: t("nodeSimpleChip.nodeTitles.taglist"),
      classname: "taglistsChip",
      textColorClassname: "taglistsChipText",
      nodeName: data.taglist?.name,
    }),
    [NODE_TYPES.PROJECT]: (data) => ({
      nodeTitle: t("nodeSimpleChip.nodeTitles.liveModel"),
      classname: "notSelectableChip",
      textColorClassname: "notSelectableText",
      nodeName: data.project?.name,
    }),
    [NODE_TYPES.SCRIPT]: (data) => ({
      nodeTitle: t("nodeSimpleChip.nodeTitles.script"),
      classname: "scriptChip",
      textColorClassname: "scriptChipText",
      nodeName: data.scriptName,
    }),
    [NODE_TYPES.END_RESULT]: endResultConfig,
    [NODE_TYPES.LABEL]: endResultConfig,
    [NODE_TYPES.FILTER]: () => ({
      nodeTitle: t("nodeSimpleChip.nodeTitles.filter"),
      classname: "notSelectableChip",
      textColorClassname: "notSelectableText",
    }),
    [NODE_TYPES.CALCULATOR]: (data) => ({
      nodeTitle: t("nodeSimpleChip.nodeTitles.calculator"),
      classname: "calculatorChip",
      textColorClassname: "calculatorChipText",
      nodeName: data.calculator?.name || data.scriptName,
    }),
    [NODE_TYPES.LOOP_START]: () => ({
      nodeTitle: t("nodeSimpleChip.nodeTitles.loopStart"),
      classname: "notSelectableChip",
      textColorClassname: "notSelectableText",
    }),
    [NODE_TYPES.LOOP_END]: () => ({
      nodeTitle: t("nodeSimpleChip.nodeTitles.loopEnd"),
      classname: "notSelectableChip",
      textColorClassname: "notSelectableText",
    }),
    [NODE_TYPES.MARKETPLACE_SCRIPT]: (data) => ({
      nodeTitle: t("nodeSimpleChip.nodeTitles.marketplaceScript"),
      classname: "marketplaceScriptChip",
      textColorClassname: "marketplaceScriptChipText",
      nodeName: data.scriptName,
    }),
  }
}

const getNodeConfig = (data: NodeSimpleChipProps["data"], t: (key: string) => string): NodeConfig => {
  const nodeConfigs = getNodeConfigs(t)
  const factory = nodeConfigs[data?.nodeType as NodeType]
  return factory(data)
}

const NodeHandle = ({ type, position }: { type: "target" | "source"; position: Position }): JSX.Element => (
  <Handle style={{ opacity: 0 }} type={type} position={position} id={type} isConnectable />
)

/**
 * NodeSimpleChip component that renders a simple chip representation of workflow nodes.
 * Displays node titles, names, and handles based on node type and selection state.
 */
export function NodeSimpleChip({ data, menuView, id }: Readonly<NodeSimpleChipProps>): React.ReactElement {
  // Translation hook
  const { t } = useTranslation("workflows")

  // Computed values
  const nodeConfig = getNodeConfig(data, t)
  const isSelectableNode = !!getComponentTypeFromNode({ data } as NodeSimpleChipProps)
  const isOtherNodeSelected = data.selectedId && data.selectedId !== id && !data.isSelected

  // Helper functions
  const getChipClassname = (): string => {
    if (data.isSelected) {
      return `${styles[nodeConfig.classname] || ""} ${styles.chip || ""} ${styles.selectedChip || ""}`
    }

    if (isOtherNodeSelected) {
      return `${styles.notSelectableChip || ""} ${styles.chip || ""}`
    }

    return `${styles[nodeConfig.classname] || ""} ${styles.chip || ""}`
  }

  const getTextColorClassname = (): string => {
    if (isOtherNodeSelected) {
      return styles.notSelectableText || ""
    }

    return styles[nodeConfig.textColorClassname] || ""
  }

  return (
    <Grid display="flex" gap={1} alignItems="center" style={{ cursor: "pointer" }}>
      {!menuView && data?.nodeType !== NODE_TYPES.START && <NodeHandle type="target" position={Position.Top} />}

      <Grid className={getChipClassname()} style={{ cursor: !isSelectableNode ? "not-allowed" : "pointer" }}>
        <Typography variant="label-bold" align="center" className={getTextColorClassname()}>
          {nodeConfig.nodeTitle}
        </Typography>
      </Grid>

      {nodeConfig.nodeName && (
        <Typography variant="a" className={isOtherNodeSelected ? styles.notSelectableText || "" : ""}>
          {nodeConfig.nodeName}
        </Typography>
      )}

      {!menuView && <NodeHandle type="source" position={Position.Bottom} />}
    </Grid>
  )
}
